import { useState, useEffect, useContext, useCallback } from 'react';

import NewSideBar from '../../../components/Navigation/NewSideBar';
import { useParams, useLocation, useOutletContext } from 'react-router-dom';
import { LayoutContext } from '../../../utils/contexts/LayoutContext';
import {
  getQuestionsByInterview,
  getVideoStatus,
} from '../../../services/Interviews/interviewService';
import VideoGeneration from './VideoGeneration';
import QuestionsTable from './QuestionsTable';
import FooterActions from './FooterActions';

const Interview = ({ embedded = false }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mode, setMode] = useState('view');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [videoStatus, setVideoStatus] = useState(null);
  const [error, setError] = useState('');

  const { uuid } = useParams();

  const { state } = useLocation();
  const outletContext = useOutletContext();

  const interviewName = outletContext?.interview?.name ?? state?.interviewName ?? 'Unnamed interview';

  const { setPadding } = useContext(LayoutContext);
  useEffect(() => {
    if (embedded) return undefined;
    setPadding('p-0');
    return () => setPadding('p-4');
  }, [embedded, setPadding]);

  const fetchData = useCallback(
    (signal) => {
      setIsLoading(true);
      setError('');
      const requests = [
        getVideoStatus({ params: { uuid } }),
        getQuestionsByInterview({
          params: { page, page_size: pageSize, uuid, question: search },
          signal,
        }),
      ];
      Promise.all(requests)
        .then(([videoStatusResponse, interviewQuestionsResponse]) => {
          setVideoStatus(videoStatusResponse);
          // const interviewQuestions = interviewQuestionsResponse.results.map(
          //   (q) => ({
          //     id: q.uuid,
          //     sequence: q.interview_sequence,
          //     interview_question_uuid: q.uuid,
          //     videos_count: q.question_videos_count,
          //     ...q.question,
          //   }),
          // );
          const rows = interviewQuestionsResponse?.results || [];
          const count = interviewQuestionsResponse?.count || 0;
          setQuestions(rows);
          setTotalCount(count);
          setPageCount(Math.ceil(count / pageSize));
        })
        .catch((err) => {
          console.error('Failed to load interview questions:', err);
          if (!signal?.aborted) setError('Unable to load interview questions. Please try again.');
        })
        .finally(() => {
          if (signal?.aborted) return;
          setIsLoading(false);
        });
    },
    [page, pageSize, search, uuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <div className={`flex h-full flex-col text-gray-900 dark:text-neutral-200 ${embedded ? '' : 'max-h-main bg-slate-50'}`}>
      <div className="flex justify-center dark:bg-gray-900 h-full">
        {!embedded && <NewSideBar />}
        <div className={`flex w-full flex-col gap-8 overflow-y-auto ${embedded ? '' : 'px-8 py-10'}`}>
          {!embedded && (
            <h1 className="text-2xl font-bold text-gray-800 underline dark:text-white">
              {interviewName}
            </h1>
          )}

          {/* <Overview totalCount={totalCount} /> */}

          <VideoGeneration
            uuid={uuid}
            videoStatus={videoStatus}
            setVideoStatus={setVideoStatus}
          />

          {error && (
            <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800">
              <p>{error}</p>
              <button type="button" onClick={() => fetchData()} className="mt-2 font-semibold underline">Try again</button>
            </div>
          )}

          <QuestionsTable
            uuid={uuid}
            interviewName={interviewName}
            questions={questions}
            setQuestions={setQuestions}
            isLoading={isLoading}
            totalCount={totalCount}
            page={page}
            setPage={setPage}
            pageCount={pageCount}
            pageSize={pageSize}
            setPageSize={setPageSize}
            search={search}
            setSearch={setSearch}
            mode={mode}
            setMode={setMode}
            selectedKeys={selectedKeys}
            setSelectedKeys={setSelectedKeys}
            videoStatus={videoStatus}
            fetchData={fetchData}
          />

          {!embedded && <FooterActions interviewName={interviewName} />}
        </div>
      </div>
    </div>
  );
};

export default Interview;
